import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Trash2, Plus, Bell, User, Mail, Heart } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useMarket } from "@/context/MarketContext";
import { ProductCard } from "@/components/ProductCard";
import { formatPrice } from "@/lib/format";

const GARAGE_IMG = "https://images.unsplash.com/photo-1485291571150-772bcfc10da5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxjYXIlMjBzaWxob3VldHRlJTIwbW9kZXJufGVufDB8fHx8MTc4NTg3MTc5OXww&ixlib=rb-4.1.0&q=85";

function Header({ title, subtitle }) {
  return (
    <div className="mb-8">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
      {subtitle && <p className="mt-2 text-slate-600">{subtitle}</p>}
    </div>
  );
}

function Shell({ children }) {
  return <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">{children}</div>;
}

export function Garage() {
  const { t } = useI18n();
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({ make: "", model: "", year: "", engine: "", nickname: "" });

  const load = () => api.get("/garage").then(({ data }) => setVehicles(data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.make || !form.model) return;
    await api.post("/garage", form);
    setForm({ make: "", model: "", year: "", engine: "", nickname: "" });
    load();
    toast.success(t("garage.add"));
  };
  const remove = async (id) => { await api.delete(`/garage/${id}`); load(); };

  return (
    <Shell>
      <Header title={t("garage.title")} subtitle={t("garage.subtitle")} />
      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div>
          {vehicles.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-white py-16 text-center text-slate-500" data-testid="garage-empty">{t("garage.empty")}</div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {vehicles.map((v) => (
                <div key={v.id} data-testid={`vehicle-${v.id}`} className="relative overflow-hidden rounded-lg border border-border bg-white p-5">
                  <img src={GARAGE_IMG} alt="" className="absolute -right-6 -top-4 h-24 w-40 object-contain opacity-10" />
                  <div className="grid h-10 w-10 place-items-center rounded-lg bg-blue-50 text-primary"><Car className="h-5 w-5" /></div>
                  <div className="mt-4 font-heading text-lg font-semibold tracking-tight text-slate-900">{v.nickname || `${v.make} ${v.model}`}</div>
                  <div className="text-sm text-slate-500">{v.make} {v.model} {v.year && `· ${v.year}`}</div>
                  {v.engine && <div className="text-xs text-slate-400">{v.engine}</div>}
                  <div className="mt-4 flex items-center gap-2">
                    <Link to={`/search?type=vehicle&q=${encodeURIComponent(v.make)}`} className="text-xs font-semibold text-primary hover:underline">{t("hero.search.button")}</Link>
                    <button onClick={() => remove(v.id)} data-testid={`vehicle-delete-${v.id}`} className="ml-auto text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <form onSubmit={add} className="h-fit rounded-lg border border-border bg-white p-6">
          <h3 className="font-heading font-semibold tracking-tight text-slate-900">{t("garage.add")}</h3>
          {["make", "model", "year", "engine", "nickname"].map((f) => (
            <input key={f} data-testid={`vehicle-${f}`} placeholder={f[0].toUpperCase() + f.slice(1)} value={form[f]}
              onChange={(e) => setForm({ ...form, [f]: e.target.value })}
              className="mt-3 w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary" />
          ))}
          <button type="submit" data-testid="vehicle-add-btn" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-blue-600">
            <Plus className="h-4 w-4" /> {t("garage.add")}
          </button>
        </form>
      </div>
    </Shell>
  );
}

export function Favorites() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { favIds } = useMarket();

  useEffect(() => {
    api.get("/favorites").then(({ data }) => setProducts(data)).finally(() => setLoading(false));
  }, [favIds]);

  return (
    <Shell>
      <Header title={t("fav.title")} subtitle={t("fav.subtitle")} />
      {loading ? <div className="py-16 text-center text-slate-400">{t("common.loading")}</div> :
        products.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-white py-16 text-center text-slate-500" data-testid="fav-empty">
            <Heart className="mx-auto mb-3 h-8 w-8 text-slate-300" />{t("fav.empty")}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
    </Shell>
  );
}

export function Alerts() {
  const { t } = useI18n();
  const [alerts, setAlerts] = useState([]);
  const load = () => api.get("/alerts").then(({ data }) => setAlerts(data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { await api.delete(`/alerts/${id}`); load(); };

  return (
    <Shell>
      <Header title={t("alerts.title")} subtitle={t("alerts.subtitle")} />
      {alerts.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-white py-16 text-center text-slate-500" data-testid="alerts-empty">
          <Bell className="mx-auto mb-3 h-8 w-8 text-slate-300" />{t("alerts.empty")}
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((a) => {
            const hit = a.current_best != null && a.current_best <= a.target_price;
            return (
              <div key={a.id} data-testid={`alert-${a.id}`} className="flex items-center gap-4 rounded-lg border border-border bg-white p-4">
                <div className={`grid h-11 w-11 place-items-center rounded-lg ${hit ? "bg-emerald-50 text-emerald-600" : "bg-blue-50 text-primary"}`}><Bell className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <Link to={`/product/${a.product_id}`} className="font-heading font-semibold tracking-tight text-slate-900 hover:text-primary">{a.product?.name}</Link>
                  <div className="text-sm text-slate-500">{t("alerts.target")}: <span className="font-medium">{formatPrice(a.target_price)}</span> · {t("alerts.current")}: <span className={`font-medium ${hit ? "text-emerald-600" : "text-slate-700"}`}>{formatPrice(a.current_best)}</span></div>
                </div>
                <button onClick={() => remove(a.id)} data-testid={`alert-delete-${a.id}`} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            );
          })}
        </div>
      )}
    </Shell>
  );
}

export function Profile() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  return (
    <Shell>
      <Header title={t("profile.title")} />
      <div className="max-w-lg rounded-lg border border-border bg-white p-8">
        <div className="flex items-center gap-4">
          <span className="grid h-16 w-16 place-items-center rounded-full bg-primary text-2xl font-bold text-white">{user?.name?.[0]?.toUpperCase()}</span>
          <div>
            <div className="font-heading text-xl font-semibold tracking-tight text-slate-900">{user?.name}</div>
            <div className="flex items-center gap-1.5 text-sm text-slate-500"><Mail className="h-3.5 w-3.5" />{user?.email}</div>
          </div>
        </div>
        <div className="mt-6 space-y-3">
          <div className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">
            <span className="flex items-center gap-2 text-slate-600"><User className="h-4 w-4" /> Role</span>
            <span className="font-medium capitalize text-slate-900">{user?.role}</span>
          </div>
        </div>
        <button onClick={logout} data-testid="profile-logout" className="mt-6 rounded-full border border-border px-6 py-2.5 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:border-red-300 hover:text-red-600">
          {t("nav.logout")}
        </button>
      </div>
    </Shell>
  );
}
